// src/pages/supplier/SupplierDashboard.jsx
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { supplierDashboardApi } from '@/api/services'
import { useAuth } from '@/context/AuthContext'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { Card, CardHeader, CardBody, StatusBadge, PageSpinner } from '@/components/common/ui'
import {
  Package, ShoppingCart, Star, Bell, ChevronRight,
  TrendingUp, FileText, Truck, Clock, CheckCircle, AlertTriangle,
  Plus, BarChart3, Store, AlertCircle,
} from 'lucide-react'
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts'

export default function SupplierDashboard() {
  const { user } = useAuth()
  const { format } = useCurrency()

  const { data: res, isLoading } = useQuery({
    queryKey: ['supplier-dashboard'],
    queryFn: () => supplierDashboardApi.index(),
    refetchInterval: 120_000,
  })

  if (isLoading) return <PageSpinner />

  const d = res?.data?.data || res?.data || {}
  const orderStats    = d.order_stats || {}
  const revenue       = d.revenue || {}
  const gmvTrend      = d.gmv_trend || []
  const topProducts   = d.top_products || []
  const recentOrders  = d.recent_orders || []
  const activeSamples = d.active_samples || []
  const sampleCounts  = d.sample_counts || {}
  const openRfqs      = d.open_rfqs || []
  const rfqStats      = d.rfq_stats || {}
  const productStats  = d.product_stats || {}
  const profilePct    = d.profile_complete?.percent || 0
  const isApproved    = d.is_approved
  const unread        = d.unread_notifications || 0
  const avgRating     = d.avg_rating
  const reviewCount   = d.review_count || 0
  const lowStock      = d.low_stock_count || 0

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-brand rounded-3xl p-7 text-white relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="font-display font-black text-2xl mb-1">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-white/80 text-sm">Here's your business overview at a glance.</p>
        </div>
        {!isApproved && (
          <div className="relative z-10 mt-3 inline-flex items-center gap-2 bg-yellow-400/20 rounded-full px-4 py-2 text-sm font-bold">
            <AlertCircle size={14} /> Pending approval — products won't be visible yet.
          </div>
        )}
        {profilePct < 100 && (
          <Link to="/supplier/profile" className="relative z-10 mt-3 inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 text-sm font-bold hover:bg-white/30 transition-colors">
            Profile {profilePct}% complete — finish setup
          </Link>
        )}
      </div>

      {/* KPI Cards Row 1 — Revenue & Orders */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Monthly Revenue"
          value={format(revenue.this_month || 0)}
          sub={`Total: ${format(revenue.total || 0)}`}
          icon={TrendingUp}
          color="pink"
        />
        <KpiCard
          label="Pending Action"
          value={orderStats.pending_action || 0}
          sub={`${orderStats.active || 0} active total`}
          icon={Clock}
          color="amber"
          to="/supplier/orders"
        />
        <KpiCard
          label="Live Products"
          value={productStats.live || 0}
          sub={`${productStats.pending_review || 0} in review`}
          icon={Package}
          color="green"
          to="/supplier/products"
        />
        <KpiCard
          label="Notifications"
          value={unread}
          sub="Unread"
          icon={Bell}
          color="blue"
          to="/supplier/notifications"
        />
      </div>

      {/* KPI Cards Row 2 — Samples, RFQs, Rating */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Sample Requests"
          value={sampleCounts.pending || 0}
          sub={`${sampleCounts.approved || 0} to dispatch`}
          icon={Truck}
          color="indigo"
          to="/supplier/samples"
        />
        <KpiCard
          label="RFQ Opportunities"
          value={rfqStats.unquoted || 0}
          sub={`${rfqStats.awarded || 0} awarded`}
          icon={FileText}
          color="purple"
          to="/supplier/rfqs"
        />
        <KpiCard
          label="Avg Rating"
          value={avgRating ? `${avgRating.toFixed(1)}/5` : '—'}
          sub={`${reviewCount} review${reviewCount !== 1 ? 's' : ''}`}
          icon={Star}
          color="orange"
        />
        <KpiCard
          label="Low Stock"
          value={lowStock}
          sub="Alerts"
          icon={AlertTriangle}
          color="teal"
          to="/supplier/products"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { to: '/supplier/products/new', label: 'Add Product',    icon: Plus },
          { to: '/supplier/orders',       label: 'Orders',         icon: ShoppingCart },
          { to: '/supplier/samples',      label: 'Samples',        icon: Truck },
          { to: '/supplier/analytics',    label: 'Analytics',      icon: BarChart3 },
          { to: '/supplier/storefront',   label: 'Storefront',     icon: Store },
        ].map(a => (
          <Link key={a.to} to={a.to}
            className="flex flex-col items-center gap-2 p-4 bg-white border border-border rounded-2xl hover:border-pink hover:shadow-card-md transition-all text-center">
            <a.icon size={20} className="text-pink" />
            <span className="text-xs font-bold">{a.label}</span>
          </Link>
        ))}
      </div>

      {/* Revenue Trend + Recent Orders */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Revenue Trend */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="font-bold text-sm">Monthly Revenue</h2>
          </CardHeader>
          <CardBody>
            {gmvTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={gmvTrend}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Revenue']} labelStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="total" stroke="#ec4899" fill="url(#revGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-10 text-muted text-sm">No revenue data yet</div>
            )}
          </CardBody>
        </Card>

        {/* Recent Orders */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <h2 className="font-bold text-sm">Recent Orders</h2>
            <Link to="/supplier/orders" className="text-pink text-xs font-bold flex items-center gap-1 hover:underline">
              View all <ChevronRight size={12} />
            </Link>
          </CardHeader>
          {recentOrders.length === 0 ? (
            <CardBody>
              <div className="text-center py-10">
                <div className="text-3xl mb-2">📦</div>
                <p className="text-sm text-muted">No orders yet</p>
                <Link to="/supplier/products/new" className="btn-primary mt-3 text-xs px-5 py-2 inline-flex">Add a Product</Link>
              </div>
            </CardBody>
          ) : (
            <CardBody className="p-0 divide-y divide-border">
              {recentOrders.map(order => (
                <Link key={order.id} to={`/supplier/orders/${order.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-pink/10 flex items-center justify-center flex-shrink-0">
                    <Package size={16} className="text-pink" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">#{order.order_number}</p>
                    <p className="text-[11px] text-muted">
                      {order.buyer?.name} · {new Date(order.created_at).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold">{format(order.total_amount)}</p>
                    <StatusBadge status={order.status} />
                  </div>
                </Link>
              ))}
            </CardBody>
          )}
        </Card>
      </div>

      {/* Top Products + Active Samples */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <Card>
          <CardHeader>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <TrendingUp size={14} className="text-green-500" /> Top Products
            </h2>
            <Link to="/supplier/analytics" className="text-pink text-xs font-bold flex items-center gap-1 hover:underline">
              Analytics <ChevronRight size={12} />
            </Link>
          </CardHeader>
          {topProducts.length === 0 ? (
            <CardBody>
              <div className="text-center py-8 text-muted text-sm">
                <p>No sales data yet</p>
                <Link to="/supplier/products/new" className="text-pink font-bold text-xs hover:underline mt-1 inline-block">Add your first product</Link>
              </div>
            </CardBody>
          ) : (
            <CardBody className="p-0 divide-y divide-border">
              {topProducts.map((p, i) => (
                <div key={p.product_id || i} className="flex items-center gap-3 px-4 py-3">
                  <span className="w-6 h-6 rounded-full bg-pink/10 text-pink text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 line-clamp-1">{p.product_name || p.name}</p>
                    <p className="text-[11px] text-muted">{p.total_qty || p.quantity} units sold</p>
                  </div>
                  <p className="text-sm font-bold text-gray-900 flex-shrink-0">{format(p.total_revenue || p.revenue)}</p>
                </div>
              ))}
            </CardBody>
          )}
        </Card>

        {/* Active Samples */}
        <Card>
          <CardHeader>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <Truck size={14} className="text-indigo-500" /> Pending Samples
            </h2>
            <Link to="/supplier/samples" className="text-pink text-xs font-bold flex items-center gap-1 hover:underline">
              View all <ChevronRight size={12} />
            </Link>
          </CardHeader>
          {activeSamples.length === 0 ? (
            <CardBody>
              <div className="text-center py-8 text-muted text-sm">No active sample requests</div>
            </CardBody>
          ) : (
            <CardBody className="p-0 divide-y divide-border">
              {activeSamples.map(sr => (
                <Link key={sr.id} to="/supplier/samples"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-surface border border-border overflow-hidden flex-shrink-0">
                    {sr.product?.primary_image_url && (
                      <img src={sr.product.primary_image_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 line-clamp-1">{sr.product?.name}</p>
                    <p className="text-[11px] text-muted">{sr.buyer?.name}</p>
                  </div>
                  <StatusBadge status={sr.status} />
                </Link>
              ))}
            </CardBody>
          )}
        </Card>
      </div>

      {/* RFQ Opportunities */}
      {openRfqs.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="font-bold text-sm flex items-center gap-2">
              <FileText size={14} className="text-purple-500" /> RFQ Opportunities
            </h2>
            <Link to="/supplier/rfqs" className="text-pink text-xs font-bold flex items-center gap-1 hover:underline">
              View all <ChevronRight size={12} />
            </Link>
          </CardHeader>
          <CardBody className="p-0 divide-y divide-border">
            {openRfqs.map(rfq => (
              <Link key={rfq.id} to={`/supplier/rfqs/${rfq.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <FileText size={16} className="text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 line-clamp-1">{rfq.title}</p>
                  <p className="text-[11px] text-muted">
                    {rfq.quantity_metres}m
                    {rfq.deadline && ` · Due ${new Date(rfq.deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                  </p>
                </div>
                <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded-full">Quote Now</span>
              </Link>
            ))}
          </CardBody>
        </Card>
      )}
    </div>
  )
}

// ── Reusable KPI Card ──────────────────────────────────────────────────────────
const COLORS = {
  pink:   'bg-pink/10 text-pink',
  blue:   'bg-blue-100 text-blue-600',
  green:  'bg-green-100 text-green-600',
  amber:  'bg-amber-100 text-amber-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  teal:   'bg-teal-100 text-teal-600',
  purple: 'bg-purple-100 text-purple-600',
  orange: 'bg-orange-100 text-orange-600',
}

function KpiCard({ label, value, sub, icon: Icon, color = 'pink', to }) {
  const Wrapper = to ? Link : 'div'
  const wrapperProps = to ? { to } : {}
  return (
    <Wrapper {...wrapperProps}
      className={`bg-white border border-border rounded-xl p-4 ${to ? 'hover:border-pink hover:shadow-card-md transition-all cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${COLORS[color]}`}>
          <Icon size={18} />
        </div>
      </div>
      <p className="text-2xl font-extrabold text-gray-900">{value}</p>
      <p className="text-[11px] text-muted font-bold uppercase tracking-wider mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-muted mt-0.5">{sub}</p>}
    </Wrapper>
  )
}
